// Runtime configuration — dev/test only.
//
// This module is the SINGLE source of truth for the API base URL
// the SPA talks to. It is hard-coded to the dev/test Worker host
// (`https://id-test.gao.domains`). The build-time env override
// `VITE_GAO_API_BASE` is allowed but the override list is narrowly
// validated so a production host can never enter the runtime
// config — even if an operator's `.env.local` (gitignored) sets
// one by mistake.
//
// A guardrail test (`src/config.test.ts`) asserts:
//   * the default is `https://id-test.gao.domains`,
//   * production hosts (`id.gao.domains`, `api.gao.domains`) are
//     rejected with a clear error message,
//   * any other override must look like a Cloudflare Worker
//     dev URL (`https://*.workers.dev`) or the canonical dev/test
//     host.

// ─── The environment map ──────────────────────────────────────────────────
//
// DEV and TEST are SEPARATE RUNTIME ENVIRONMENTS with separate hosts, separate
// issuers and separate SIWE identities. This module used to encode "loopback,
// or else TEST", which made the DEV issuer unreachable by construction: a
// console pointed at `id-dev.gao.domains` was refused by its own allowlist, and
// every non-loopback build signed for `admin-test.gao.domains` whatever host it
// was served from.
//
// Each environment is now one reviewed tuple. Nothing is derived from
// `window.location`, so a framed or proxied page cannot move the identity, and
// nothing is accepted from a caller beyond choosing WHICH of these tuples the
// build is for.
//
// Domain standard, cf. `wrangler.toml` and `gao-id-worker/deploy/*`:
//   DEV   → admin-dev.gao.domains   ↔ id-dev.gao.domains
//   TEST  → admin-test.gao.domains  ↔ id-test.gao.domains
//   PROD  → operator-only repository; absent here and refused below.

/** A deployed environment this console can be built for. */
export type GaoEnvironment = "dev" | "test";

export interface EnvironmentTuple {
  /** Where this console is served. */
  readonly adminOrigin: string;
  /** The Gao ID authority it talks to. */
  readonly apiBase: string;
  /** The EIP-4361 `domain` it signs for. */
  readonly siweDomain: string;
  /** The EIP-4361 `uri` it signs for. */
  readonly siweUri: string;
}

const ENVIRONMENTS: Readonly<Record<GaoEnvironment, EnvironmentTuple>> = {
  dev: {
    adminOrigin: "https://admin-dev.gao.domains",
    apiBase: "https://id-dev.gao.domains",
    siweDomain: "admin-dev.gao.domains",
    siweUri: "https://admin-dev.gao.domains",
  },
  test: {
    adminOrigin: "https://admin-test.gao.domains",
    apiBase: "https://id-test.gao.domains",
    siweDomain: "admin-test.gao.domains",
    siweUri: "https://admin-test.gao.domains",
  },
};

export class UnknownEnvironmentError extends Error {
  constructor(public readonly attempted: string) {
    super(
      `gao-admin-web has no environment named "${attempted}". ` +
        `VITE_GAO_ENV may be "dev" or "test".`,
    );
  }
}

/**
 * Which environment is this build for?
 *
 * An absent value means TEST, which is what every build produced before this
 * map existed and what the current prepare/deploy lane inlines. A value that is
 * present but not a known environment is refused rather than quietly treated as
 * TEST — a typo must not silently ship a console pointed at the wrong issuer.
 */
export function resolveEnvironment(env?: { VITE_GAO_ENV?: string }): GaoEnvironment {
  const raw = env?.VITE_GAO_ENV;
  if (typeof raw !== "string" || raw === "") return "test";
  const name = raw.trim().toLowerCase();
  if (name === "dev" || name === "test") return name;
  throw new UnknownEnvironmentError(raw);
}

function readEnv(): { VITE_GAO_ENV?: string; VITE_GAO_API_BASE?: string; VITE_GAO_SIWE_ORIGIN?: string } {
  if (typeof import.meta === "undefined" || !import.meta.env) return {};
  return {
    VITE_GAO_ENV: import.meta.env.VITE_GAO_ENV as string | undefined,
    VITE_GAO_API_BASE: import.meta.env.VITE_GAO_API_BASE as string | undefined,
    VITE_GAO_SIWE_ORIGIN: import.meta.env.VITE_GAO_SIWE_ORIGIN as string | undefined,
  };
}

/** The environment this bundle was built for. */
export const GAO_ENVIRONMENT: GaoEnvironment = resolveEnvironment(readEnv());

/** The full tuple for the environment this bundle was built for. */
export const ENVIRONMENT: EnvironmentTuple = ENVIRONMENTS[GAO_ENVIRONMENT];

/** The tuple for a named environment. Exported for the guardrail tests. */
export function environmentTuple(name: GaoEnvironment): EnvironmentTuple {
  return ENVIRONMENTS[name];
}

const FORBIDDEN_PRODUCTION_HOSTS: ReadonlySet<string> = new Set([
  "id.gao.domains",
  "api.gao.domains",
]);

/**
 * The loopback hosts. They are the only hosts allowed over plain `http:`.
 *
 * A same-origin local acceptance stack (Vite at `http://localhost:5173`
 * proxying `/v2` to a `wrangler dev` Worker) has no TLS and no reason to
 * grow one; refusing `http:` there forced every local login to be debugged on
 * a deployed tier instead. The relaxation is exactly this pair, by exact
 * hostname: `http://evil.localhost`, `http://id-test.gao.domains` and any
 * `http:` production host stay refused, and the production deny-set below is
 * checked before any allow rule.
 */
const LOOPBACK_HOSTS: ReadonlySet<string> = new Set(["localhost", "127.0.0.1"]);

/** Returns `true` if `u` is an HTTPS URL pointing at one of the
 *  allowed dev/test targets, or an HTTP(S) URL on a loopback host. */
function isAllowedDevTestBase(u: string, environment: GaoEnvironment): boolean {
  let parsed: URL;
  try {
    parsed = new URL(u);
  } catch {
    return false;
  }
  if (FORBIDDEN_PRODUCTION_HOSTS.has(parsed.hostname)) return false;
  // Loopback is the one place plain http is honest: the dev server and the
  // local Worker share a machine. Nothing else is reachable over http.
  if (LOOPBACK_HOSTS.has(parsed.hostname)) {
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  }
  if (parsed.protocol !== "https:") return false;
  // The issuer of the environment this build is FOR — and only that one. A DEV
  // build may not be pointed at the TEST issuer, nor the reverse: they are
  // different authorities with different operator mappings and different
  // allowlists, and a console that could straddle them would make "which tier
  // did this act touch" unanswerable from the artifact.
  if (parsed.hostname === ENVIRONMENTS[environment].apiBase.replace("https://", "")) {
    return true;
  }
  if (parsed.hostname.endsWith(".workers.dev")) return true;
  return false;
}

export class ProductionUrlForbiddenError extends Error {
  constructor(public readonly attempted: string) {
    super(
      `gao-admin-web is dev/test only; refusing to use API base "${attempted}". ` +
        `Allowed: https://id-test.gao.domains, https://*.workers.dev, http(s)://localhost*, http(s)://127.0.0.1*. ` +
        `Forbidden: id.gao.domains, api.gao.domains.`,
    );
  }
}

export function resolveApiBase(env?: { VITE_GAO_API_BASE?: string; VITE_GAO_ENV?: string }): string {
  const environment = resolveEnvironment(env);
  const candidate =
    typeof env?.VITE_GAO_API_BASE === "string" && env.VITE_GAO_API_BASE !== ""
      ? env.VITE_GAO_API_BASE
      : ENVIRONMENTS[environment].apiBase;
  if (!isAllowedDevTestBase(candidate, environment)) {
    throw new ProductionUrlForbiddenError(candidate);
  }
  return candidate.replace(/\/+$/, "");
}

export const API_BASE = resolveApiBase(readEnv());

/** The TEST environment's issuer — used by guardrail tests + the README
 *  banner. NEVER changes without an explicit PR + audit trail. */
export const DEVTEST_API_BASE = ENVIRONMENTS.test.apiBase;

/** The DEV environment's issuer. */
export const DEV_API_BASE = ENVIRONMENTS.dev.apiBase;

/**
 * Canonical dev/test login audience the SPA sends on every
 * `/v2/admin/owner/auth/challenge` request. The BE stores the
 * audience on the challenge row and re-checks it on verify, so
 * any stable non-empty string the SPA chooses is fine — but it
 * must match exactly between the two requests. We use the SPA's
 * own custom domain so the audience is traceable from the BE
 * audit row.
 */
export const ADMIN_LOGIN_AUDIENCE = ENVIRONMENT.adminOrigin;

/**
 * The SIWE `domain` and `uri` this console signs with.
 *
 * They must match `gao-id-worker`'s SIWE_DOMAIN and ALLOWED_ORIGINS exactly, and
 * they are constants rather than `window.location` readings on purpose: a
 * framed, proxied or wrapped page must not be able to produce a message for an
 * origin this deployment never intended to authorize.
 */
// Per-environment, from the map above. A DEV console signs as
// `admin-dev.gao.domains`; a TEST console signs as `admin-test.gao.domains`.
// Neither can sign as the other, and no build can sign as production.
const DEFAULT_SIWE_URI = ENVIRONMENT.siweUri;

export class SiweOriginForbiddenError extends Error {
  constructor(public readonly attempted: string) {
    super(
      `gao-admin-web refuses to sign for origin "${attempted}". ` +
        `VITE_GAO_SIWE_ORIGIN may only name a loopback origin (http(s)://localhost[:port], http(s)://127.0.0.1[:port]); ` +
        `every deployed console signs for ${DEFAULT_SIWE_URI}.`,
    );
  }
}

/**
 * Resolve the SIWE origin this build signs for.
 *
 * The deployed console signs for its own host, above, and no env value can
 * move it: the override is honoured ONLY for a loopback origin, so a local
 * acceptance stack can tell the wallet the truth (`localhost:5173 wants you to
 * sign in`) and the local Worker can verify it against a SIWE_DOMAIN that
 * admits exactly that. It is still a build-time constant — read once here,
 * never from `window.location` — so a framed or proxied page changes nothing.
 * The issuer remains the authority: a message for an origin its allowlists do
 * not carry is refused server-side whatever this function returns.
 */
export function resolveSiweOrigin(env?: {
  VITE_GAO_SIWE_ORIGIN?: string;
  VITE_GAO_ENV?: string;
}): {
  domain: string;
  uri: string;
} {
  const tuple = ENVIRONMENTS[resolveEnvironment(env)];
  const raw = env?.VITE_GAO_SIWE_ORIGIN;
  if (typeof raw !== "string" || raw === "") {
    return { domain: tuple.siweDomain, uri: tuple.siweUri };
  }
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    throw new SiweOriginForbiddenError(raw);
  }
  const httpish = parsed.protocol === "http:" || parsed.protocol === "https:";
  const bareOrigin =
    parsed.pathname === "/" && parsed.search === "" && parsed.hash === "" && parsed.username === "";
  if (!httpish || !bareOrigin || !LOOPBACK_HOSTS.has(parsed.hostname)) {
    throw new SiweOriginForbiddenError(raw);
  }
  // `host` carries the port when one is present, which is what EIP-4361's
  // `domain` (an RFC 3986 authority) expects; `origin` is scheme://host.
  return { domain: parsed.host, uri: parsed.origin };
}

const SIWE_ORIGIN = resolveSiweOrigin(readEnv());

export const ADMIN_SIWE_DOMAIN = SIWE_ORIGIN.domain;
export const ADMIN_SIWE_URI = SIWE_ORIGIN.uri;

/** The TEST console's SIWE pair. Exported for the guardrail tests. */
export const DEVTEST_SIWE_DOMAIN = ENVIRONMENTS.test.siweDomain;
export const DEVTEST_SIWE_URI = ENVIRONMENTS.test.siweUri;

/** The DEV console's SIWE pair. */
export const DEV_SIWE_DOMAIN = ENVIRONMENTS.dev.siweDomain;
export const DEV_SIWE_URI = ENVIRONMENTS.dev.siweUri;

// ─── Two different chains, and conflating them was a defect ────────────────
//
// The console used to hold ONE chain constant, 84532, and use it for two
// unrelated questions: "where does GaoSafe authority live" and "what chain must
// the wallet be on to log in". Those have different answers, and answering the
// second with the first made the login screen tell an operator on Base mainnet
// that their correct configuration was a "Wrong chain" error — then offer to
// "fix" it by switching them to the authority chain.
//
// Under the ratified chain law the wallet ADDRESS is the Admin login identity.
// The connected chain is protocol metadata that binds the SIWE nonce, nothing
// more. The GaoSafe authority chain is decided server-side and is never a
// constraint on the wallet.

/**
 * The GaoSafe AUTHORITY chain — where the server reads live vault membership.
 *
 * Mirrors `gao-id-worker`'s `GAOSAFE_AUTHORITY_CHAIN_ID`. It is presentation-only
 * here: the server never trusts a chain the browser names, and this value must
 * NEVER gate, compare against, or switch the connected wallet. It exists so the
 * post-login authority panel can say where authority is read from.
 */
export const GAOSAFE_AUTHORITY_CHAIN_ID = 84532;
export const GAOSAFE_AUTHORITY_CHAIN_NAME = "Base Sepolia";

/**
 * The canonical Gao SIWE login-chain allowlist.
 *
 * Mirrors `gao-id-worker`'s `CHAIN_ID_ALLOWLIST`, which `verifyAndConsume`
 * enforces server-side — this copy decides only what the UI PRESENTS as
 * acceptable, and a chain the server would refuse must not look acceptable here.
 * If the backend list changes, this changes with it.
 */
export const SIWE_CHAIN_ALLOWLIST: readonly number[] = [
  1, 10, 56, 97, 137, 8453, 42161, 84532, 421614, 11155111, 11155420,
];

/** Human names for the chains an operator is plausibly on. Presentation only. */
const CHAIN_NAMES: Readonly<Record<number, string>> = {
  1: "Ethereum",
  10: "OP Mainnet",
  56: "BNB Chain",
  97: "BNB Testnet",
  137: "Polygon",
  8453: "Base",
  42161: "Arbitrum One",
  84532: "Base Sepolia",
  421614: "Arbitrum Sepolia",
  11155111: "Sepolia",
  11155420: "OP Sepolia",
};

/**
 * May an operator log in from this connected chain?
 *
 * The allowlist is the ONLY rule. There is deliberately no comparison against
 * `GAOSAFE_AUTHORITY_CHAIN_ID` — being on the authority chain is neither
 * required nor special.
 */
export function isAllowedLoginChain(id: number | null | undefined): id is number {
  return typeof id === "number" && SIWE_CHAIN_ALLOWLIST.includes(id);
}

/** A chain's display label. Falls back to the bare id for an unknown chain. */
export function chainLabel(id: number | null | undefined): string {
  if (typeof id !== "number") return "Unknown";
  return CHAIN_NAMES[id] ?? `Chain ${id}`;
}

/**
 * RETAINED FOR THE SETTLEMENT SURFACE ONLY — not for login.
 *
 * `AffiliateWithdrawalDetail` uses these to name the chain a withdrawal settles
 * on, which genuinely is 84532. Nothing on the login path may read them; the
 * login-chain rule is `isAllowedLoginChain`.
 */
export const REQUIRED_CHAIN_ID = GAOSAFE_AUTHORITY_CHAIN_ID;
export const REQUIRED_CHAIN_NAME = GAOSAFE_AUTHORITY_CHAIN_NAME;

/**
 * Default `ownerSubject` for the dev/test owner login. A PREFILL, never a
 * constraint: it saves retyping when the AuthRequired guard bounces an operator
 * back here, and the field stays editable.
 *
 * It names no wallet, deliberately. Any CURRENT GaoSafe owner may authenticate —
 * there are several legitimate owner wallets — and which one is signing is
 * settled by the signature the server recovers and the live `isOwner` read it
 * performs, never by anything this bundle believes. A console that pinned a
 * single address here would refuse a legitimate owner for no reason it could
 * justify.
 */
export const DEFAULT_OWNER_SUBJECT = "op-cb-2026";

/**
 * Optional WalletConnect / Reown project ID. The Admin Web
 * deliberately does NOT hardcode a fallback (per the operator's
 * documented rule "never hardcode fallbacks" — the canonical
 * project ID lives in 1Password and is supplied at build time).
 * When this env var is unset, the SPA renders the WalletConnect
 * option as BLOCKED with a clear "operator must set
 * VITE_WALLETCONNECT_PROJECT_ID at build time" notice.
 */
export const WALLETCONNECT_PROJECT_ID: string | null =
  typeof import.meta !== "undefined" &&
  typeof import.meta.env?.VITE_WALLETCONNECT_PROJECT_ID === "string" &&
  import.meta.env.VITE_WALLETCONNECT_PROJECT_ID !== ""
    ? (import.meta.env.VITE_WALLETCONNECT_PROJECT_ID as string)
    : null;
